import React, {
    useState,
    useEffect,
    useRef,
    useContext,
    useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import "mapbox-gl/dist/mapbox-gl.css";
import distributionMapIcon from "../assets/icons/analytics.png";
import digitalIcon from "../assets/icons/digital_twin.png";
import defectsNavIcon from "../assets/icons/select_defect_list.svg";
import mapboxgl from "mapbox-gl";
import { AssetContext } from "../contexts/AssetListContext.js";
import BackButton from "../components/BackButton.js";
import Config from "../config.json";
import { riskRatings, buildCircleColorExpression } from "../utils/riskConfig";
const MAPBOX_TOKEN =
    "pk.eyJ1Ijoibmlrb2FuZHJlIiwiYSI6ImNtODU3MXJqcjAwcGkya3B1N3F6bzBiamoifQ.z8RU8-lQD7c0EBUGdfM_0g";

const CIRCLE_COLOR_EXPRESSION = buildCircleColorExpression();

/**
 * Defect distribution map (satellite + risk-coloured points / clustering).
 * Primary nav matches landing: Digital Twin | Distribution (here) | Defects.
 */
const Distribution = () => {
    const { defects } = useContext(AssetContext);
    const navigate = useNavigate();
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const clusterPopupRef = useRef(null);
    const pointPopupRef = useRef(null);
    const clusterMouseMoveHandlerRef = useRef(null);
    const clusterMouseLeaveHandlerRef = useRef(null);
    const unclusteredClickHandlerRef = useRef(null);
    const unclusteredMouseMoveHandlerRef = useRef(null);
    const unclusteredMouseEnterHandlerRef = useRef(null);
    const unclusteredMouseLeaveHandlerRef = useRef(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);

    const clearMapInteractions = useCallback(() => {
        if (!mapRef.current) return;

        if (clusterMouseMoveHandlerRef.current) {
            mapRef.current.off(
                "mousemove",
                "clusters",
                clusterMouseMoveHandlerRef.current,
            );
            clusterMouseMoveHandlerRef.current = null;
        }

        if (clusterMouseLeaveHandlerRef.current) {
            mapRef.current.off(
                "mouseleave",
                "clusters",
                clusterMouseLeaveHandlerRef.current,
            );
            clusterMouseLeaveHandlerRef.current = null;
        }

        if (unclusteredClickHandlerRef.current) {
            mapRef.current.off(
                "click",
                "unclustered-point",
                unclusteredClickHandlerRef.current,
            );
            unclusteredClickHandlerRef.current = null;
        }

        if (unclusteredMouseMoveHandlerRef.current) {
            mapRef.current.off(
                "mousemove",
                "unclustered-point",
                unclusteredMouseMoveHandlerRef.current,
            );
            unclusteredMouseMoveHandlerRef.current = null;
        }

        if (unclusteredMouseEnterHandlerRef.current) {
            mapRef.current.off(
                "mouseenter",
                "unclustered-point",
                unclusteredMouseEnterHandlerRef.current,
            );
            unclusteredMouseEnterHandlerRef.current = null;
        }

        if (unclusteredMouseLeaveHandlerRef.current) {
            mapRef.current.off(
                "mouseleave",
                "unclustered-point",
                unclusteredMouseLeaveHandlerRef.current,
            );
            unclusteredMouseLeaveHandlerRef.current = null;
        }

        mapRef.current.getCanvas().style.cursor = "";

        if (clusterPopupRef.current) {
            clusterPopupRef.current.remove();
            clusterPopupRef.current = null;
        }
        if (pointPopupRef.current) {
            pointPopupRef.current.remove();
            pointPopupRef.current = null;
        }
    }, []);

    const bindMapInteractions = useCallback(
        (clusterMode) => {
            if (!mapRef.current) return;

            clearMapInteractions();

            if (clusterMode && mapRef.current.getLayer("clusters")) {
                clusterMouseMoveHandlerRef.current = (event) => {
                    const feature = event.features?.[0];
                    if (!feature) return;

                    const coordinates = [...feature.geometry.coordinates];
                    const defectCount = feature.properties?.point_count || 0;

                    if (!clusterPopupRef.current) {
                        clusterPopupRef.current = new mapboxgl.Popup({
                            closeButton: false,
                            closeOnClick: false,
                            offset: 12,
                        });
                    }

                    mapRef.current.getCanvas().style.cursor = "pointer";

                    clusterPopupRef.current
                        .setLngLat(coordinates)
                        .setHTML(`<strong>${defectCount}</strong> defects`)
                        .addTo(mapRef.current);
                };

                clusterMouseLeaveHandlerRef.current = () => {
                    mapRef.current.getCanvas().style.cursor = "";
                    if (clusterPopupRef.current) {
                        clusterPopupRef.current.remove();
                        clusterPopupRef.current = null;
                    }
                };

                mapRef.current.on(
                    "mousemove",
                    "clusters",
                    clusterMouseMoveHandlerRef.current,
                );
                mapRef.current.on(
                    "mouseleave",
                    "clusters",
                    clusterMouseLeaveHandlerRef.current,
                );
            } else if (
                !clusterMode &&
                mapRef.current.getLayer("unclustered-point")
            ) {
                unclusteredClickHandlerRef.current = (event) => {
                    const feature = event.features?.[0];
                    const defectId = feature?.properties?.defect_id;
                    if (defectId) {
                        navigate(`/defect-details/${defectId}`);
                    }
                };
                unclusteredMouseMoveHandlerRef.current = (event) => {
                    const feature = event.features?.[0];
                    if (!feature) return;

                    const coordinates = [...feature.geometry.coordinates];
                    const defectId = feature.properties?.defect_id || "N/A";
                    const assetName = feature.properties?.asset_name || "N/A";
                    const component =
                        feature.properties?.component ||
                        feature.properties?.area ||
                        "N/A";

                    if (!pointPopupRef.current) {
                        pointPopupRef.current = new mapboxgl.Popup({
                            closeButton: false,
                            closeOnClick: false,
                            offset: 12,
                        });
                    }

                    pointPopupRef.current
                        .setLngLat(coordinates)
                        .setHTML(
                            `<div style="color:#242424;font-size:13px;line-height:1.4;">
                            <div style="margin:0;"><strong>Defect ID:</strong> ${defectId}</div>
                            <div style="margin:4px 0 0;"><strong>Asset:</strong> ${assetName}</div>
                            <div style="margin:4px 0 0;"><strong>Component:</strong> ${component}</div>
                        </div>`,
                        )
                        .addTo(mapRef.current);
                };

                mapRef.current.on(
                    "click",
                    "unclustered-point",
                    unclusteredClickHandlerRef.current,
                );
                mapRef.current.on(
                    "mousemove",
                    "unclustered-point",
                    unclusteredMouseMoveHandlerRef.current,
                );
                unclusteredMouseEnterHandlerRef.current = () => {
                    mapRef.current.getCanvas().style.cursor = "pointer";
                };
                unclusteredMouseLeaveHandlerRef.current = () => {
                    mapRef.current.getCanvas().style.cursor = "";
                    if (pointPopupRef.current) {
                        pointPopupRef.current.remove();
                        pointPopupRef.current = null;
                    }
                };
                mapRef.current.on(
                    "mouseenter",
                    "unclustered-point",
                    unclusteredMouseEnterHandlerRef.current,
                );
                mapRef.current.on(
                    "mouseleave",
                    "unclustered-point",
                    unclusteredMouseLeaveHandlerRef.current,
                );
            }
        },
        [clearMapInteractions, navigate],
    );

    const toggleSwitch = () => {
        if (mapRef.current && mapLoaded) {
            const newIsEnabled = !isEnabled;
            setIsEnabled(newIsEnabled);

            if (mapRef.current.getLayer("unclustered-point")) {
                mapRef.current.removeLayer("unclustered-point");
            }
            if (mapRef.current.getLayer("clusters")) {
                mapRef.current.removeLayer("clusters");
            }
            if (mapRef.current.getLayer("cluster-count")) {
                mapRef.current.removeLayer("cluster-count");
            }
            if (mapRef.current.getSource("points")) {
                mapRef.current.removeSource("points");
            }

            const geojsonData = {
                type: "FeatureCollection",
                features: defects.map((defect) => ({
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates: [defect.lon, defect.lat],
                    },
                    properties: {
                        defect_name: defect.defect_name,
                        defect_id: defect.defect_id,
                        asset_name: defect.asset_name,
                        component: defect.component,
                        area: defect.area,
                        risk_rate: defect.risk_rate,
                    },
                })),
            };

            mapRef.current.addSource("points", {
                type: "geojson",
                data: geojsonData,
                cluster: newIsEnabled,
                clusterMaxZoom: 22,
                clusterRadius: 50,
            });

            if (newIsEnabled) {
                mapRef.current.addLayer({
                    id: "unclustered-point",
                    type: "circle",
                    source: "points",
                    filter: ["!", ["has", "point_count"]],
                    paint: {
                        "circle-color": "rgba(140, 18, 181, 0.4)",
                        "circle-radius": 5,
                        "circle-stroke-width": 2,
                        "circle-stroke-color": "#8c12b5",
                    },
                });

                mapRef.current.addLayer({
                    id: "clusters",
                    type: "circle",
                    source: "points",
                    filter: ["has", "point_count"],
                    paint: {
                        "circle-color": "rgba(140, 18, 181, 0.4)",
                        "circle-radius": [
                            "interpolate",
                            ["linear"],
                            ["get", "point_count"],
                            1,
                            5,
                            3,
                            15,
                            6,
                            30,
                            7,
                            40,
                            8,
                            50,
                        ],
                        "circle-stroke-width": 2,
                        "circle-stroke-color": "#8c12b5",
                    },
                });
                bindMapInteractions(true);
            } else {
                mapRef.current.addLayer({
                    id: "unclustered-point",
                    type: "circle",
                    source: "points",
                    filter: ["!", ["has", "point_count"]],
                    paint: {
                        "circle-color": CIRCLE_COLOR_EXPRESSION,
                        "circle-radius": 5,
                    },
                });
                bindMapInteractions(false);
            }
        }
    };

    useEffect(() => {
        if (defects.length > 0 && !mapRef.current) {
            mapboxgl.accessToken = MAPBOX_TOKEN;
            mapRef.current = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: "mapbox://styles/mapbox/satellite-streets-v11",
                center: [defects[0].lon, defects[0].lat],
                zoom: 22,
                minZoom: 12,
                maxZoom: 22,
                attributionControl: false,
                dragRotate: false,
            });

            mapRef.current.on("load", () => {
                setMapLoaded(true);

                const geojsonData = {
                    type: "FeatureCollection",
                    features: defects.map((defect) => ({
                        type: "Feature",
                        geometry: {
                            type: "Point",
                            coordinates: [defect.lon, defect.lat],
                        },
                        properties: {
                            defect_name: defect.defect_name,
                            defect_id: defect.defect_id,
                            asset_name: defect.asset_name,
                            component: defect.component,
                            area: defect.area,
                            risk_rate: defect.risk_rate,
                        },
                    })),
                };

                mapRef.current.addSource("points", {
                    type: "geojson",
                    data: geojsonData,
                    cluster: false,
                });

                mapRef.current.addLayer({
                    id: "unclustered-point",
                    type: "circle",
                    source: "points",
                    filter: ["!", ["has", "point_count"]],
                    paint: {
                        "circle-color": CIRCLE_COLOR_EXPRESSION,
                        "circle-radius": 5,
                    },
                });
                bindMapInteractions(false);
            });

            const bounds = defects.reduce(
                (bounds, defect) => {
                    return bounds.extend([defect.lon, defect.lat]);
                },
                new mapboxgl.LngLatBounds(
                    [defects[0].lon, defects[0].lat],
                    [defects[0].lon, defects[0].lat],
                ),
            );

            mapRef.current.fitBounds(bounds, {
                padding: 100,
                maxZoom: 50,
                duration: 1000,
            });
        }
    }, [bindMapInteractions, defects]);

    useEffect(() => {
        return () => {
            clearMapInteractions();
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [clearMapInteractions]);

    return (
        <div className="map-container">
            <div className="landing-container">
                <div className="first-group">
                    <h1>{Config.title}</h1>
                    <h5 style={{ color: "#FFF" }}>
                        {isEnabled
                            ? "Defect Density"
                            : "Defect Distribution by Risk Rate"}
                    </h5>
                </div>
                <div className="second-group">
                    <BackButton className="map-back-button" />
                    <Link to="/digital-twin" className="link-button">
                        <img
                            src={digitalIcon}
                            alt=""
                            className="button-image"
                        />
                        <div>
                            <h6>Digital Twin</h6>
                            <span className="button-text">Digital Asset</span>
                        </div>
                    </Link>
                    <div
                        className="link-button distribution-nav-current"
                        aria-current="page"
                    >
                        <img
                            src={distributionMapIcon}
                            alt=""
                            className="button-image"
                        />
                        <div>
                            <h6>Distribution</h6>
                            <span className="button-text">
                                Defects on the map
                            </span>
                        </div>
                    </div>
                    <Link to="/defects" className="link-button">
                        <img
                            src={defectsNavIcon}
                            alt=""
                            className="button-image"
                        />
                        <div>
                            <h6>Defects</h6>
                            <span className="button-text">Defect table</span>
                        </div>
                    </Link>
                </div>
                <div className="analytics-switch">
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={toggleSwitch}
                        />
                        <span className="slider round"></span>
                    </label>
                </div>
                <div
                    className="analytics-risk-rate"
                    style={{ display: isEnabled ? "none" : "flex" }}
                >
                    <h6>Risk Rate</h6>
                    <div className="analytics-risk-rate-section">
                        {riskRatings.map((r) => (
                            <div key={r.label} style={{ display: "flex", gap: "10px" }}>
                                <div className="risk-square" style={{ backgroundColor: r.color }}></div>
                                <p>{r.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div
                ref={mapContainerRef}
                style={{ height: "100%", width: "100%" }}
            />
        </div>
    );
};

export default Distribution;
